import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import AddIcon from '@mui/icons-material/Add';
import GridOnIcon from '@mui/icons-material/GridOn';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';

// 3-level hierarchy: Left Nav → View → Config[]
interface LeftSidebarProps {
  navData: Record<string, Record<string, string[]>>;  // leftNav -> { viewLabel -> configName[] }
  viewMeta?: Record<string, { viewType?: string | null; itemCount?: number | null }>;  // key: `${leftNav}|${viewLabel}|${configName}`
  activeSection: string;       // selected leftNav
  onSectionChange: (section: string) => void;
  activeView: string;          // selected viewLabel
  activeConfig: string;        // selected configName (the item that loads data)
  onConfigSelect: (leftNav: string, view: string, config: string) => void;
  onViewLabelSelect?: (leftNav: string, view: string) => void;  // narrow matrix to viewLabel folder
  collapsed: boolean;
  onToggle: () => void;
  onNewView?: () => void;      // opens NewViewDialog (parent provides)
}

const LeftSidebar: React.FC<LeftSidebarProps> = ({
  navData,
  viewMeta,
  activeSection,
  onSectionChange,
  activeView,
  activeConfig,
  onConfigSelect,
  onViewLabelSelect,
  onNewView,
  collapsed,
  onToggle,
}) => {
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [expandedViews, setExpandedViews] = useState<Set<string>>(new Set());

  // Auto-expand the section + view containing the active selection so a
  // refresh that restores (activeSection, activeView) shows the user's full
  // visual path — not a collapsed sidebar with the selection invisible.
  useEffect(() => {
    if (activeSection) {
      setCollapsedSections(prev => {
        if (!prev.has(activeSection)) return prev;
        const next = new Set(prev);
        next.delete(activeSection);
        return next;
      });
    }
    if (activeSection && activeView) {
      const key = `${activeSection}::${activeView}`;
      setExpandedViews(prev => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    }
  }, [activeSection, activeView]);

  // Resizable sidebar width. Persists in localStorage so it survives reloads.
  const SIDEBAR_WIDTH_KEY = 'viewManager.sidebarWidth';
  const MIN_WIDTH = 200;
  const MAX_WIDTH = 600;
  const [width, setWidth] = useState<number>(() => {
    const saved = parseInt(localStorage.getItem(SIDEBAR_WIDTH_KEY) || '', 10);
    return Number.isFinite(saved) && saved >= MIN_WIDTH && saved <= MAX_WIDTH ? saved : 260;
  });

  // Drag-to-resize. Captures pointer on the handle, listens at the window level
  // so the drag survives even if the cursor moves over child elements.
  const handleResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    const onMove = (ev: PointerEvent) => {
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + (ev.clientX - startX)));
      setWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      // Persist final width.
      setWidth((w) => {
        localStorage.setItem(SIDEBAR_WIDTH_KEY, String(w));
        return w;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const toggleSection = (section: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
    onSectionChange(section);
  };

  const toggleView = (viewKey: string) => {
    setExpandedViews((prev) => {
      const next = new Set(prev);
      if (next.has(viewKey)) {
        next.delete(viewKey);
      } else {
        next.add(viewKey);
      }
      return next;
    });
  };

  const isSectionExpanded = (section: string) => !collapsedSections.has(section);
  const isViewExpanded = (viewKey: string) => expandedViews.has(viewKey);

  if (collapsed) {
    return (
      <Box
        sx={{
          width: 36,
          flexShrink: 0,
          borderRight: '1px solid #E0E0E0',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          pt: 1,
        }}
      >
        <IconButton size="small" onClick={onToggle}>
          <ChevronRight sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        borderRight: '1px solid #E0E0E0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          borderBottom: '1px solid #E0E0E0',
          background: 'white',
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            color: '#555',
            letterSpacing: 0.5,
          }}
        >
          Navigation
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {onNewView && (
            <Tooltip title="Create new view in this section">
              <IconButton size="small" onClick={onNewView}>
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onToggle}>
            <ChevronLeft sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable 3-level tree */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
        {Object.entries(navData).map(([section, viewsMap]) => {
          const isSectionActive = section === activeSection;
          const sectionExpanded = isSectionExpanded(section);

          return (
            <Box key={section}>
              {/* Level 1: Left Nav section */}
              <Box
                onClick={() => toggleSection(section)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  px: 1.5,
                  py: 0.75,
                  cursor: 'pointer',
                  backgroundColor: isSectionActive ? '#E8F5E9' : 'transparent',
                  borderLeft: isSectionActive ? '3px solid #00897B' : '3px solid transparent',
                  '&:hover': {
                    backgroundColor: isSectionActive ? '#E8F5E9' : '#F5F5F5',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                    color: isSectionActive ? '#00695C' : '#444',
                    textAlign: 'left',
                    flex: 1,
                  }}
                >
                  {section}
                </Typography>
                {sectionExpanded ? (
                  <ExpandLess sx={{ fontSize: 18, color: '#888', flexShrink: 0 }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 18, color: '#888', flexShrink: 0 }} />
                )}
              </Box>

              {/* Level 2: Views under this section — always expandable */}
              {sectionExpanded &&
                Object.entries(viewsMap).map(([viewLabel, configs]) => {
                  const viewKey = `${section}::${viewLabel}`;
                  const isViewActive = section === activeSection && viewLabel === activeView;
                  const viewExpanded = isViewExpanded(viewKey);

                  return (
                    <Box key={viewKey}>
                      {/* View folder — click ONLY expands/collapses. Earlier this
                          also called onViewLabelSelect to scope the matrix to
                          this folder, but the side effect was that opening the
                          dropdown auto-selected the folder and shifted the
                          right pane to the matrix view — making it look like
                          two clicks were needed to drill into a specific
                          config. Pure expansion matches user expectation. */}
                      <Box
                        onClick={() => {
                          toggleView(viewKey);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          pl: 3.5,
                          pr: 1.5,
                          py: 0.5,
                          cursor: 'pointer',
                          backgroundColor: isViewActive ? '#E8F5E9' : 'transparent',
                          '&:hover': {
                            backgroundColor: isViewActive ? '#E8F5E9' : '#F5F5F5',
                          },
                        }}
                      >
                        <FolderOutlined
                          sx={{
                            fontSize: 15,
                            color: isViewActive ? '#00897B' : '#999',
                            mr: 0.75,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: isViewActive ? 600 : 500,
                            color: isViewActive ? '#00695C' : '#555',
                            textAlign: 'left',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {viewLabel}
                        </Typography>
                        {/* Show Coverage Matrix for this folder. Separate
                            click target so expanding the folder doesn't
                            auto-scope the matrix. stopPropagation prevents
                            the surrounding folder click handler from also
                            firing (which would toggle expansion). */}
                        <Tooltip title="Show Coverage Matrix for this folder">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewLabelSelect?.(section, viewLabel);
                            }}
                            sx={{
                              p: 0.25,
                              mr: 0.25,
                              color: isViewActive ? '#00695C' : '#AAA',
                              '&:hover': { color: '#00695C', backgroundColor: 'rgba(0,137,123,0.08)' },
                              flexShrink: 0,
                            }}
                          >
                            <GridOnIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        {viewExpanded ? (
                          <ExpandLess sx={{ fontSize: 16, color: '#AAA', flexShrink: 0 }} />
                        ) : (
                          <ExpandMore sx={{ fontSize: 16, color: '#AAA', flexShrink: 0 }} />
                        )}
                      </Box>

                      {/* Level 3: Configs under this view — these are the clickable leaves */}
                      {viewExpanded &&
                        configs.map((config) => {
                          const isConfigActive = isSectionActive && viewLabel === activeView && config === activeConfig;
                          const meta = viewMeta?.[`${section}|${viewLabel}|${config}`];
                          const typeLabel = meta?.viewType || null;
                          const itemCount = meta?.itemCount;

                          return (
                            <Box
                              key={`${viewKey}::${config}`}
                              onClick={() => onConfigSelect(section, viewLabel, config)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                pl: 5.5,
                                pr: 1.5,
                                py: 0.4,
                                cursor: 'pointer',
                                backgroundColor: isConfigActive ? '#E0F2F1' : 'transparent',
                                '&:hover': {
                                  backgroundColor: isConfigActive ? '#E0F2F1' : '#F5F5F5',
                                },
                              }}
                            >
                              <DescriptionOutlined
                                sx={{
                                  fontSize: 13,
                                  color: isConfigActive ? '#00695C' : '#BBB',
                                  mr: 0.75,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  fontWeight: isConfigActive ? 600 : 400,
                                  color: isConfigActive ? '#00695C' : '#777',
                                  textAlign: 'left',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                {config}
                              </Typography>
                              {typeLabel && (
                                <Chip
                                  label={typeLabel}
                                  size="small"
                                  sx={{
                                    height: 16,
                                    fontSize: 9,
                                    ml: 0.5,
                                    '& .MuiChip-label': { px: 0.75 },
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                              {typeof itemCount === 'number' && itemCount >= 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: 9,
                                    color: '#999',
                                    ml: 0.5,
                                    flexShrink: 0,
                                  }}
                                >
                                  {itemCount}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                    </Box>
                  );
                })}
            </Box>
          );
        })}
      </Box>

      {/* Drag-to-resize handle on the right edge */}
      <Box
        onPointerDown={handleResizeStart}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 6,
          height: '100%',
          cursor: 'col-resize',
          // Subtle visual cue on hover so the affordance is discoverable.
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
          '&:active': { backgroundColor: 'rgba(0,0,0,0.16)' },
          userSelect: 'none',
          touchAction: 'none',
        }}
      />
    </Box>
  );
};

export default LeftSidebar;
